import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { anthropometricService } from '../services';
import { Calendar, Trash2, ArrowLeft } from 'lucide-react';
import Swal from 'sweetalert2';

const AnthropometricDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [measurement, setMeasurement] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadMeasurement = async () => {
    try {
      const data = await anthropometricService.get(id);
      setMeasurement(data);
    } catch (error) {
      console.error('Error loading measurement:', error);
      Swal.fire({
        title: 'Error',
        text: 'No se pudo cargar la medición',
        icon: 'error',
        confirmButtonColor: '#ef4444',
      });
      navigate('/anthropometric');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMeasurement();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleDelete = async () => {
    const result = await Swal.fire({
      title: '¿Eliminar medición?',
      text: 'Esta acción no se puede deshacer',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar',
    });

    if (result.isConfirmed) {
      try {
        await anthropometricService.delete(id);
        await Swal.fire({
          title: '¡Eliminado!',
          text: 'Medición eliminada correctamente',
          icon: 'success',
          confirmButtonColor: '#8b5cf6',
        });
        navigate('/anthropometric');
      } catch (error) {
        console.error('Error deleting measurement:', error);
        Swal.fire({
          title: 'Error',
          text: 'No se pudo eliminar la medición',
          icon: 'error',
          confirmButtonColor: '#ef4444',
        });
      }
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    });
  };

  const Section = ({ title, icon, children }) => (
    <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
      <div className="flex items-center gap-3 mb-6 pb-4 border-b-2 border-purple-100">
        <span className="text-3xl">{icon}</span>
        <h2 className="text-2xl font-bold text-gray-900">{title}</h2>
      </div>
      {children}
    </div>
  );

  const DataRow = ({ label, value, unit }) => {
    if (!value || value === '0.00') return null;
    return (
      <div className="flex justify-between items-center py-3 border-b border-gray-100 hover:bg-purple-50 px-4 rounded-lg transition-colors">
        <span className="text-gray-700 font-medium">{label}</span>
        <span className="text-gray-900 font-bold text-lg">
          {parseFloat(value).toFixed(2)} <span className="text-purple-600">{unit}</span>
        </span>
      </div>
    );
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-purple-600 mx-auto mb-4"></div>
            <p className="text-gray-600 font-semibold">Cargando medición...</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (!measurement) {
    return (
      <Layout>
        <div className="text-center py-20">
          <p className="text-gray-500 text-xl">Medición no encontrada</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="animate-fadeIn max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/anthropometric')}
            className="mb-4 text-purple-600 hover:text-purple-800 font-bold flex items-center gap-2"
          >
            <ArrowLeft size={20} />
            Volver
          </button>
          
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-5xl font-extrabold mb-2 text-gray-900">
                📏 Detalle de Medición
              </h1>
              <div className="flex items-center gap-2 text-gray-600 text-lg">
                <Calendar size={20} />
                <span className="font-semibold">{formatDate(measurement.measurement_date)}</span>
              </div>
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={handleDelete}
                className="bg-red-500 hover:bg-red-600 text-white p-3 rounded-xl transition-all shadow-lg"
                title="Eliminar"
              >
                <Trash2 size={20} />
              </button>
            </div>
          </div>
        </div>

        {/* Calculated Values - Highlight Section */}
        <Section title="Valores Calculados" icon="🎯">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {measurement.bmi && (
              <div 
                className="p-6 rounded-xl text-center"
                style={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  boxShadow: '0 10px 25px rgba(102, 126, 234, 0.3)',
                }}
              >
                <div className="text-white mb-2 font-semibold">IMC (BMI)</div>
                <div className="text-4xl font-bold text-white mb-1">
                  {parseFloat(measurement.bmi).toFixed(1)}
                </div>
                <div className="text-purple-100 text-sm">
                  {parseFloat(measurement.bmi) < 18.5 ? 'Bajo peso' :
                   parseFloat(measurement.bmi) < 25 ? 'Peso normal' :
                   parseFloat(measurement.bmi) < 30 ? 'Sobrepeso' : 'Obesidad'}
                </div>
              </div>
            )}

            {measurement.body_fat_percentage && (
              <div 
                className="p-6 rounded-xl text-center"
                style={{
                  background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                  boxShadow: '0 10px 25px rgba(240, 147, 251, 0.3)',
                }}
              >
                <div className="text-white mb-2 font-semibold">% Grasa Corporal</div>
                <div className="text-4xl font-bold text-white mb-1">
                  {parseFloat(measurement.body_fat_percentage).toFixed(1)}%
                </div>
                <div className="text-pink-100 text-sm">
                  {parseFloat(measurement.body_fat_percentage) < 15 ? 'Atlético' :
                   parseFloat(measurement.body_fat_percentage) < 20 ? 'Fitness' :
                   parseFloat(measurement.body_fat_percentage) < 25 ? 'Aceptable' : 'Mejorar'}
                </div>
              </div>
            )}

            {measurement.waist_hip_ratio && (
              <div 
                className="p-6 rounded-xl text-center"
                style={{
                  background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                  boxShadow: '0 10px 25px rgba(79, 172, 254, 0.3)',
                }}
              >
                <div className="text-white mb-2 font-semibold">Índice Cintura-Cadera</div>
                <div className="text-4xl font-bold text-white mb-1">
                  {parseFloat(measurement.waist_hip_ratio).toFixed(2)}
                </div>
                <div className="text-blue-100 text-sm">
                  {parseFloat(measurement.waist_hip_ratio) < 0.9 ? 'Bajo riesgo' :
                   parseFloat(measurement.waist_hip_ratio) < 1.0 ? 'Moderado' : 'Alto riesgo'}
                </div>
              </div>
            )}

            {measurement.lean_mass && (
              <div 
                className="p-6 rounded-xl text-center"
                style={{
                  background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
                  boxShadow: '0 10px 25px rgba(67, 233, 123, 0.3)',
                }}
              >
                <div className="text-white mb-2 font-semibold">Masa Magra</div>
                <div className="text-4xl font-bold text-white mb-1">
                  {parseFloat(measurement.lean_mass).toFixed(1)}
                </div>
                <div className="text-green-100 text-sm">kg</div>
              </div>
            )}

            {measurement.fat_mass && (
              <div 
                className="p-6 rounded-xl text-center"
                style={{
                  background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
                  boxShadow: '0 10px 25px rgba(250, 112, 154, 0.3)',
                }}
              >
                <div className="text-white mb-2 font-semibold">Masa Grasa</div>
                <div className="text-4xl font-bold text-white mb-1">
                  {parseFloat(measurement.fat_mass).toFixed(1)}
                </div>
                <div className="text-yellow-100 text-sm">kg</div>
              </div>
            )}
          </div>
        </Section>

        {/* Basic Measurements */}
        <Section title="Medidas Básicas" icon="📊">
          <DataRow label="Peso" value={measurement.weight} unit="kg" />
          <DataRow label="Talla (Altura)" value={measurement.height} unit="cm" />
          <DataRow label="Talla Sentado" value={measurement.sitting_height} unit="cm" />
          <DataRow label="Envergadura" value={measurement.arm_span} unit="cm" />
        </Section>

        {/* Perimeters */}
        {(measurement.perimeter_wrist || measurement.perimeter_arm_relaxed || 
          measurement.perimeter_arm_flexed || measurement.perimeter_waist_min ||
          measurement.perimeter_waist_umbilical || measurement.perimeter_hip_max ||
          measurement.perimeter_thigh_mid || measurement.perimeter_calf_max) && (
          <Section title="Perímetros" icon="⭕">
            <DataRow label="Muñeca" value={measurement.perimeter_wrist} unit="cm" />
            <DataRow label="Brazo Relajado" value={measurement.perimeter_arm_relaxed} unit="cm" />
            <DataRow label="Brazo en Contracción" value={measurement.perimeter_arm_flexed} unit="cm" />
            <DataRow label="Cintura Mínima" value={measurement.perimeter_waist_min} unit="cm" />
            <DataRow label="Cintura Umbilical" value={measurement.perimeter_waist_umbilical} unit="cm" />
            <DataRow label="Cadera Máxima" value={measurement.perimeter_hip_max} unit="cm" />
            <DataRow label="Muslo Medio" value={measurement.perimeter_thigh_mid} unit="cm" />
            <DataRow label="Pierna Máximo" value={measurement.perimeter_calf_max} unit="cm" />
          </Section>
        )}

        {/* Diameters */}
        {(measurement.diameter_wrist || measurement.diameter_humerus || measurement.diameter_femur) && (
          <Section title="Diámetros Óseos" icon="📐">
            <DataRow label="Muñeca" value={measurement.diameter_wrist} unit="cm" />
            <DataRow label="Húmero" value={measurement.diameter_humerus} unit="cm" />
            <DataRow label="Fémur" value={measurement.diameter_femur} unit="cm" />
          </Section>
        )}

        {/* Skinfolds */}
        {(measurement.skinfold_triceps || measurement.skinfold_subscapular ||
          measurement.skinfold_biceps || measurement.skinfold_iliac_crest ||
          measurement.skinfold_supraspinal || measurement.skinfold_abdominal ||
          measurement.skinfold_front_thigh || measurement.skinfold_medial_calf) && (
          <Section title="Pliegues Cutáneos" icon="📏">
            <DataRow label="Tríceps" value={measurement.skinfold_triceps} unit="mm" />
            <DataRow label="Subescapular" value={measurement.skinfold_subscapular} unit="mm" />
            <DataRow label="Bíceps" value={measurement.skinfold_biceps} unit="mm" />
            <DataRow label="Cresta Ilíaca" value={measurement.skinfold_iliac_crest} unit="mm" />
            <DataRow label="Supraespinal" value={measurement.skinfold_supraspinal} unit="mm" />
            <DataRow label="Abdominal" value={measurement.skinfold_abdominal} unit="mm" />
            <DataRow label="Muslo Anterior" value={measurement.skinfold_front_thigh} unit="mm" />
            <DataRow label="Pierna Medial" value={measurement.skinfold_medial_calf} unit="mm" />
          </Section>
        )}

        {/* Notes */}
        {measurement.notes && (
          <Section title="Notas" icon="📝">
            <p className="text-gray-700 leading-relaxed bg-gray-50 p-4 rounded-xl">
              {measurement.notes}
            </p>
          </Section>
        )}
      </div>
    </Layout>
  );
};

export default AnthropometricDetail;
